import React, { useCallback, useMemo, useState } from 'react';

import { Button } from 'primereact/button';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import { Tooltip } from 'primereact/tooltip';
import { useFormContext } from 'react-hook-form';
import { AttainmentWithDates } from 'types/interfaces/Attainment';
import { DropdownWithDeletedValueTemplate } from 'utils/DropdownWithDeletedValueTemplate';

import { TreeNode } from '../types/interfaces';

import NewDatepicker from './custom-controls/NewDatepicker';
import DeleteConfirmationDialog from './shared/DeleteConfirmationDialog';

interface AttainmentViewProps {
  passedClassName?: string;
  index: number;
  item: AttainmentWithDates;
  options: TreeNode[];
  onDelete: () => void;
  onUpdate: (updated: AttainmentWithDates) => void;
  errors?: any;
  placeholder?: string;
  useDateOnlyFormat?: boolean;
}

export const AttainmentViewComponent: React.FC<AttainmentViewProps> = ({
  passedClassName = '',
  index,
  item,
  options,
  onDelete,
  onUpdate,
  errors,
  placeholder = 'Выберите навык',
  useDateOnlyFormat = true
}) => {
  const { trigger } = useFormContext();
  const [confirmVisible, setConfirmVisible] = useState(false);

  const memoizedOptions = useMemo(
    () => (Array.isArray(options) ? options : []).map(o => ({ ...o, label: o.name })),
    [options]
  );

  const selectedAttainment = useMemo(() => {
    const found = memoizedOptions.find(c => c.id?.toString() === item.attainmentId?.toString());
    if (found) return found;
    if (item.attainmentId || item.name || '') {
      return { id: item.attainmentId, label: item.name || 'Выберите навык' };
    }
    return undefined;
  }, [memoizedOptions, item.attainmentId, item.name]);


  const isDuration = !!item.duration || !!(selectedAttainment as any)?.duration;

  const formatDateForPicker = (date?: string) => (date ? date.split('T')[0] : '');

  const handleAttainmentSelect = useCallback(
    (e: DropdownChangeEvent) => {
      const selectedNode = e.value;

      const isDuration = !!selectedNode?.duration;

      const update: AttainmentWithDates = {
        ...item,
        attainmentId: selectedNode?.id || 0,
        name: selectedNode?.label || item.name || '', 
        startDate: isDuration ? item.startDate : undefined,
        endDate: isDuration ? item.endDate : undefined
      };

      onUpdate(update);
      trigger(`attainments.${index}.attainmentId`);
    },
    [item, index, onUpdate, trigger]
  );
  const handleDateChange = useCallback(
    (dates: { dateFrom: string; dateUntil: string }) => {
      const update: AttainmentWithDates = {
        ...item,
        startDate: dates.dateFrom,
        endDate: dates.dateUntil
      };

      onUpdate(update);
      trigger([`attainments.${index}.startDate`, `attainments.${index}.endDate`]);
    },
    [item, index, onUpdate, trigger]
  );

  const dropdownPlaceholder = !item.attainmentId ? placeholder : selectedAttainment?.label || placeholder;

  return (
    <div className={`${passedClassName} flex flex-row justify-content-between w-auto attainment-item`}>
      <div className="flex p-align-center gap-1">
        <div className="p-col-3" style={{ width: isDuration ? '559px' : '860px' }}>
          <Dropdown
            value={selectedAttainment}
            options={memoizedOptions}
            onChange={handleAttainmentSelect}
            optionLabel="label"
            placeholder={dropdownPlaceholder}
            className={`w-full ${errors?.attainmentId ? 'p-invalid' : ''}`}
            filter
            showClear
            itemTemplate={(option) => <div title={option.description || option.label}>{option.label}</div>}
            valueTemplate={(option) => (
              <DropdownWithDeletedValueTemplate 
                option={option} 
                selectedItem={selectedAttainment}
                placeholder={dropdownPlaceholder}
              />
            )}
          />
          <p className={`asset-type-card-errors card-errors ${errors?.attainmentId ? 'text-red-500' : 'text-transparent'}`}>
            {errors?.attainmentId ? errors.attainmentId.message : ' '}
          </p>
        </div>

        {isDuration && (
          <div className="p-col">
            <NewDatepicker
              customWidth="147px"
              onChange={handleDateChange}
              datepickerDate={{
                dateFrom: formatDateForPicker(item.startDate),
                dateUntil: formatDateForPicker(item.endDate)
              }}
              errors={errors}
              tooltip={{ left: 'Дата начала действия навыка', right: 'Дата завершения действия навыка' }}
              gap="gap-1"
              useDateOnlyFormat={useDateOnlyFormat}
            />
          </div>
        )}

        <div>
          <Button icon="pi pi-trash" className="p-button-danger" onClick={() => setConfirmVisible(true)} />
        </div>
      </div>

      <DeleteConfirmationDialog
        visible={confirmVisible}
        onHide={() => setConfirmVisible(false)}
        onConfirm={onDelete}
      >
        Вы уверены, что хотите удалить навык?
      </DeleteConfirmationDialog>
    </div>
  );
};

export default AttainmentViewComponent;
